/**
 * Which accent actually governs — the practice's brand colour, or this member's own closed-enum
 * pick — given the member's stored `accentSource` preference and the practice's own branding.
 *
 * A pure decision function, per `claude-kit/rules/invariants.md`'s "Business rules live in pure,
 * tested modules — never inline in a component": `ThemeProvider.tsx` is the one place this actually
 * drives a DOM write, but the decision itself is worth testing directly rather than only through a
 * rendered component.
 *
 * Three real inputs compose here, and the priority order is the whole point:
 *
 *   1. If the practice has no brand colour at all, there is nothing to defer to — always
 *      `'closed-enum'`.
 *   2. If the practice has a brand colour **and forbids the override** (`allowUserColorPicker:
 *      false`), the practice's colour always wins, regardless of what this member has stored —
 *      `claude-kit/rules/invariants.md`'s "an individual user may override the company's colour
 *      scheme for themselves... though that isn't the recommended default" cuts both ways: the
 *      practice is the one that decides whether the override exists at all.
 *   3. Otherwise, the member's own stored `accentSource` decides — `'org'` (the default,
 *      `preference.ts`'s `DEFAULT_ACCENT_SOURCE`) defers to the practice's colour; `'personal'`
 *      means this member explicitly picked their own accent via `ThemeMenu.tsx` and it should keep
 *      winning until they choose to defer back.
 */

import type { AccentSource } from './preference.ts';

export type ResolvedAccentSource = 'org' | 'closed-enum';

export interface OrgAccentPolicy {
  readonly brandColor: string | null;
  readonly allowUserColorPicker: boolean;
}

export function resolveAccentSource(
  storedSource: AccentSource,
  orgBranding: OrgAccentPolicy | null,
): ResolvedAccentSource {
  if (orgBranding === null || orgBranding.brandColor === null) return 'closed-enum';
  if (!orgBranding.allowUserColorPicker) return 'org';
  return storedSource === 'org' ? 'org' : 'closed-enum';
}
